import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useApp } from '../app/AppContext';
import { ApiError, loginPassword, resendVerification, upgradeGuest, verifyEmail } from '../lib/api';
import type { Contact } from '../lib/contact';

/**
 * Post-order "save your details" card (PLAN_GUEST_CHECKOUT G-P3). Shown once
 * to a guest who just placed an order: email + password turn the guest into
 * an account IN PLACE (same user id — this order and any future ones stay
 * together). Existing email → sign in, which links the guest instead.
 */
export function SaveDetailsCard({ slug, contact, onDone }: { slug: string; contact: Contact; onDone: () => void }) {
  const { reload, toast } = useApp();
  const [step, setStep] = useState<'offer' | 'verify' | 'done'>('offer');
  const [email, setEmail] = useState(contact.email);
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [exists, setExists] = useState(false);

  const create = async () => {
    setBusy(true); setError(null); setExists(false);
    try {
      await upgradeGuest(email.trim(), password, contact.name);
      setStep('verify');
      toast('We emailed you a 6-digit code.', 'info');
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) setExists(true);
      setError(e instanceof Error ? e.message : 'Could not create the account.');
    } finally { setBusy(false); }
  };

  const verify = async () => {
    setBusy(true); setError(null);
    try {
      await verifyEmail(email.trim(), code.trim());
      // Fresh member tokens for the same user (the guest tokens still work, but
      // sign in so the session is a real account from here on).
      await loginPassword(email.trim(), password);
      await reload();
      setStep('done');
      toast('Account created — next time is one tap.', 'ok');
      onDone();
    } catch (e) { setError(e instanceof Error ? e.message : 'Could not verify that code.'); }
    finally { setBusy(false); }
  };

  if (step === 'done') return null;

  return (
    <div className="card account-card">
      <h2>{step === 'offer' ? 'Save your details for next time?' : 'Check your email'}</h2>
      {step === 'offer' && (
        <>
          <p className="muted">Create an account with the details you just used — no forms next time, and your orders stay together. Optional.</p>
          <form onSubmit={(e) => { e.preventDefault(); void create(); }}>
            <label>Email<input className="input" type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} /></label>
            <label>Choose a password<input className="input" type="password" autoComplete="new-password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} /></label>
            {error && <p className="error">{error}</p>}
            {exists && <p><Link className="btn btn-sm" to={`/${slug}/login`} state={{ returnTo: `/${slug}/orders`, email }}>Sign in to add this order to your account</Link></p>}
            <div className="row wrap" style={{ gap: 12 }}>
              <button className="btn" type="submit" disabled={busy}>{busy ? 'Creating…' : 'Create account'}</button>
              <button className="btn btn-ghost" type="button" onClick={onDone} disabled={busy}>No thanks</button>
            </div>
          </form>
        </>
      )}
      {step === 'verify' && (
        <form onSubmit={(e) => { e.preventDefault(); void verify(); }}>
          <p className="muted">Enter the code we sent to {email}.</p>
          <label>6-digit code<input className="input" inputMode="numeric" pattern="[0-9]{6}" required value={code} onChange={(e) => setCode(e.target.value)} /></label>
          {error && <p className="error">{error}</p>}
          <div className="row wrap" style={{ gap: 12 }}>
            <button className="btn" type="submit" disabled={busy}>{busy ? 'Checking…' : 'Verify'}</button>
            <button className="link" type="button" onClick={() => void resendVerification(email.trim()).then(() => toast('Code re-sent.', 'info'))}>Resend code</button>
            <button className="link" type="button" onClick={onDone}>Later</button>
          </div>
        </form>
      )}
    </div>
  );
}
